import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Paper,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { newsService } from '@/services/news/newsService';
import { News } from '@/constants/types';
import { PdfViewer } from '@/components/dashboard/feed/FeedMedia';
import moment from 'moment';

type AttachmentInfo = {
  url: string;
  isImage: boolean;
  isPdf: boolean;
  fileName?: string;
  contentType?: string;
};

const getAttachment = (thumb: News['thumbnail']): AttachmentInfo | null => {
  if (!thumb) return null;
  if (typeof thumb === 'string') {
    return { url: thumb, isImage: true, isPdf: false };
  }
  if (!thumb.url) return null;
  const ct = (thumb.contentType || '').toLowerCase();
  const explicitType = (thumb.type || '').toLowerCase();
  const isImage = explicitType === 'image' || ct.startsWith('image/');
  const isPdf = ct.includes('pdf') || (thumb.fileName || '').toLowerCase().endsWith('.pdf');
  return { url: thumb.url, isImage, isPdf, fileName: thumb.fileName, contentType: thumb.contentType };
};

export const NewsDetailPage: React.FC = () => {
  const { newsId } = useParams<{ newsId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (newsId) {
      fetchNewsDetail();
    }
  }, [newsId]);

  const fetchNewsDetail = async () => {
    setLoading(true);
    try {
      const response = await newsService.getNewsById(newsId!);
      if (response.success) {
        setNews(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch news detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!news) {
    return (
      <Box textAlign="center" py={10}>
        <Typography variant="h5">News not found</Typography>
        <Button onClick={() => navigate('/news')} sx={{ mt: 2 }}>
          Back to News
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/news')}
        sx={{ mb: 3, textTransform: 'none' }}
      >
        Back to News
      </Button>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            mb: 3,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2,
          }}
        >
          {news.title}
        </Typography>

        <Box display="flex" alignItems="center" gap={3} mb={4} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {moment(news.createdAt).format('MMMM DD, YYYY')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              src={news.userId?.profilePicture?.url}
              sx={{ width: 24, height: 24 }}
            >
              {news.userId?.name.charAt(0)}
            </Avatar>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              {news.userId?.name}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {(() => {
          const attachment = getAttachment(news.thumbnail);
          if (!attachment) return null;
          return (
            <Box sx={{ mb: 4 }}>
              {attachment.isImage ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxHeight: 500,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : '#1d1d1d',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${attachment.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'blur(25px)',
                      transform: 'scale(1.2)',
                      opacity: 0.6,
                    }}
                  />
                  <Box
                    component="img"
                    src={attachment.url}
                    alt={attachment.fileName || news.title}
                    sx={{
                      position: 'relative',
                      display: 'block',
                      width: '100%',
                      maxHeight: 500,
                      objectFit: 'contain',
                      zIndex: 1,
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                  }}
                >
                  {attachment.isPdf ? (
                    <PdfViewer url={attachment.url} fileName={attachment.fileName} />
                  ) : (
                    <Box
                      component="iframe"
                      src={`https://docs.google.com/viewer?url=${encodeURIComponent(attachment.url)}&embedded=true`}
                      sx={{ width: '100%', height: 600, border: 'none', display: 'block' }}
                      title={attachment.fileName || 'Document'}
                    />
                  )}
                </Box>
              )}
            </Box>
          );
        })()}

        <Box
          className="news-content"
          sx={{
            fontSize: '1.1rem',
            lineHeight: 1.6,
            color: 'text.primary',
            '& p': { mb: 2 },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
          }}
          dangerouslySetInnerHTML={{ __html: news.content }}
        />
      </Paper>
    </Box>
  );
};
